import { lazy } from 'react';

/**
 * Dynamic imports centralization
 *
 * This module centralizes all dynamic imports for the application,
 * making it easier to manage code splitting and lazy loading.
 *
 * It contains two main categories of imports:
 * 1. UI Component Imports - Generic UI components like Popover
 * 2. Search Functionality Imports - Search-specific components like SearchProvider
 *
 * Benefits:
 * - Improved initial load performance by deferring non-critical resources
 * - Cleaner component code by separating import logic
 * - Better maintainability through centralized dynamic import management
 * - Consistent approach to lazy loading across the application
 */

// ============================================================================
// UI COMPONENT IMPORTS
// ============================================================================

/**
 * Creates a lazy-loaded component from a popover module export
 */
function createPopoverComponent(exportName: string) {
   return lazy(() =>
      import('../../components/popover').then(module => ({
         default: module[exportName as keyof typeof module],
      }))
   );
}

/**
 * Lazy-loaded Popover components
 * These are imported as static exports to ensure they're loaded together
 * with other dynamic components in a single batch
 */
export const DynamicPopover = createPopoverComponent('Popover');
export const DynamicPopoverTrigger = createPopoverComponent('PopoverTrigger');
export const DynamicPopoverContent = createPopoverComponent('PopoverContent');

// ============================================================================
// SEARCH FUNCTIONALITY IMPORTS
// ============================================================================

/**
 * Dynamically import SearchProvider component
 * This is loaded only when search functionality is actually needed
 */
export const DynamicSearchProvider = lazy(() =>
   import('../components/search-provider').then(module => ({
      default: module.default,
   }))
);

/**
 * Dynamically import SearchContent component
 * This ensures the component is loaded based on the same trigger as other dynamic components
 */
export const DynamicSearchContent = lazy(() =>
   import('../components/search-content').then(module => ({
      default: module.SearchContent,
   }))
);

// ============================================================================
// BULK PRELOADING UTILITY
// ============================================================================

/**
 * Preload all dynamic components to ensure they're downloaded in a single batch
 * This should be called when search functionality is about to be used
 * to trigger all lazy imports simultaneously
 */
export const preloadAllSearchComponents = async (): Promise<void> => {
   try {
      await Promise.all([
         // UI Components
         import('../../components/popover'),
         // Search Components
         import('../components/search-provider'),
         import('../components/search-content'),
      ]);
   } catch (error) {
      console.warn('Failed to preload search components:', error);
   }
};
